import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CaretRight } from '@phosphor-icons/react';
import { Icon } from '../../../../../../components/Icon';
import {
  EditFlagDrawer,
  FlagHistoryDrawer,
  type FlagEdit,
} from '../../../../../../components/settings/feature-flag-drawers';
import { SettingCard } from '../../../../../../components/settings/setting-card';
import { FilterChips } from '../../../../../../components/ui/filter-chips';
import { SearchInput } from '../../../../../../components/ui/input';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { Toggle } from '../../../../../../components/ui/toggle';
import { useToast } from '../../../../../../components/ui/toast';
import { formatEdited } from '../../../../../../lib/format-edited';
import { flagsQuery, useSaveFlag } from '../../../../../../lib/workspace-api';
import type { FeatureFlagRecord } from '@mybusinessmate/schema';
import type { TagTone } from '@mybusinessmate/ui-tokens';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '../../../../../../components/ui/skeleton';
import { NoResults, ErrorState } from '../../../../../../components/ui/state-views';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/flags')({
  component: FlagsPage,
});

type Filter = 'all' | 'on' | 'off';

function audienceTag(flag: FeatureFlagRecord): { label: string; tone: TagTone } {
  if (!flag.enabled) return { label: 'OFF', tone: 'grey' };
  switch (flag.audience) {
    case 'everyone':
      return { label: 'EVERYONE', tone: 'indigo' };
    case 'share':
      return { label: `${flag.sharePercent}% OF CLIENTS`, tone: 'blue' };
    case 'staff':
      return { label: 'STAFF ONLY', tone: 'amber' };
    case 'businesses':
      return {
        label: `${flag.businessIds.length} ${flag.businessIds.length === 1 ? 'BUSINESS' : 'BUSINESSES'}`,
        tone: 'pink',
      };
  }
}

function FlagsPage() {
  const toast = useToast();
  const flagList = useQuery(flagsQuery);
  const flags = flagList.data ?? [];
  const saveFlag = useSaveFlag();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [editingKey, setEditingKey] = useState<string>();
  const [historyKey, setHistoryKey] = useState<string>();

  const on = flags.filter((f) => f.enabled);
  const off = flags.filter((f) => !f.enabled);
  const q = search.trim().toLowerCase();
  const rows = (filter === 'on' ? on : filter === 'off' ? off : flags).filter(
    (f) => !q || f.name.toLowerCase().includes(q) || f.key.includes(q),
  );

  const editing = flags.find((f) => f.key === editingKey);
  const history = flags.find((f) => f.key === historyKey);

  const quickToggle = (flag: FeatureFlagRecord, enabled: boolean) =>
    saveFlag.mutate(
      { key: flag.key, input: { ...flag, enabled } },
      { onSuccess: () => toast({ title: `${flag.name} is ${enabled ? 'on' : 'off'}` }) },
    );

  const save = (edit: FlagEdit) => {
    if (!editing) return;
    saveFlag.mutate(
      { key: editing.key, input: edit },
      {
        onSuccess: () => {
          setEditingKey(undefined);
          toast({ title: 'Flag saved', description: 'Changes apply within a minute.' });
        },
      },
    );
  };

  return (
    <>
      <SettingCard
        title="Feature flags"
        subtitle="Turn features on for everyone, some clients, or staff only. Changes apply within a minute."
      >
        <div className="flex flex-wrap items-center gap-(--mbm-space-3) pb-(--mbm-space-3)">
          <SearchInput
            containerClassName="min-w-(--mbm-layout-dialog-width) flex-1 sm:max-w-(--mbm-layout-dialog-width)"
            placeholder="Search flags"
            aria-label="Search flags"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
          <FilterChips<Filter>
            aria-label="Filter flags"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All', count: flags.length },
              { value: 'on', label: 'On', count: on.length },
              { value: 'off', label: 'Off', count: off.length },
            ]}
          />
        </div>

        {flagList.isLoading ? (
          <Skeleton className="h-(--mbm-size-list-row-tall) rounded-(--mbm-radius-md)" />
        ) : flagList.isError ? (
          <ErrorState title="Couldn’t load the feature flags" onRetry={() => void flagList.refetch()} />
        ) : rows.length === 0 ? (
          <NoResults
            query={search}
            description="Try a different name or filter."
            onClear={() => {
              setSearch('');
              setFilter('all');
            }}
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border-line border-t border-border-line">
            {rows.map((flag) => {
              const tag = audienceTag(flag);
              return (
                <li key={flag.key} className="flex items-center gap-(--mbm-space-4) py-(--mbm-space-4)">
                  <button
                    type="button"
                    onClick={() => setEditingKey(flag.key)}
                    aria-label={`Edit ${flag.name}`}
                    className="flex min-w-0 flex-1 flex-col items-start rounded-(--mbm-radius-xs) text-left focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                  >
                    <span className="text-label-large text-text-primary">{flag.name}</span>
                    <span className="text-code text-text-tertiary">{flag.key}</span>
                    <span className="text-body-small text-text-tertiary">
                      {flag.updatedBy
                        ? `${flag.enabled ? 'Edited' : 'Turned off'} by ${flag.updatedBy} · ${formatEdited(flag.updatedAt ?? '')}`
                        : flag.enabled
                          ? 'Default setting'
                          : 'Not released yet'}
                    </span>
                  </button>
                  <StatusTag label={tag.label} tone={tag.tone} variant="soft" className="self-center" />
                  <Toggle
                    aria-label={`${flag.name} is on`}
                    checked={flag.enabled}
                    onCheckedChange={(v) => quickToggle(flag, v)}
                  />
                  <button
                    type="button"
                    aria-label={`Open ${flag.name}`}
                    onClick={() => setEditingKey(flag.key)}
                    className="rounded-full text-text-secondary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                  >
                    <Icon icon={CaretRight} size="sm" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </SettingCard>

      <EditFlagDrawer
        flag={editing}
        onClose={() => setEditingKey(undefined)}
        onSave={save}
        onHistory={() => setHistoryKey(editingKey)}
      />
      <FlagHistoryDrawer flag={history} onClose={() => setHistoryKey(undefined)} />
    </>
  );
}
